import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AnimatePresence, motion, useIsPresent } from "framer-motion";
import {
  ArrowLeft,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  Loader2,
  MapPin,
  Search,
  X,
} from "lucide-react";
import { type MouseEvent, useEffect, useId, useMemo, useRef, useState } from "react";
import { useSearchVenues, useServiceCategories } from "@/api/hooks";
import type { SearchLocationMode } from "@/api/types";
import { Chip } from "@/components/ui/chip";
import type { RecentSearch, SearchBarState } from "@/features/search/types";
import { DEBOUNCE_MS, TIME_OPTIONS } from "@/features/search/types";
import { useDebounce } from "@/hooks/use-debounce";
import { cn } from "@/lib/cn";
import { getMaxDate, isBeyondMaxDate, isSameDay, parseISODate, toISODate } from "@/lib/dates";
import {
  KEYBOARD_SCROLL_CONTAINER_ATTR,
  MOBILE_VISUAL_VIEWPORT_SHELL_CLASS,
} from "@/lib/overlay-keyboard-layout";
import { SearchFailure } from "../search-failure";
import { getCategoryIcon } from "./category-icons";
import { parseCategoryKeys } from "./category-selection";
import { MobileLocationOverlay, type SelectedLocation } from "./mobile-location-overlay";
import {
  DEFAULT_LOCATION_LABEL,
  formatRecentSearchLabel,
  isDefaultLocationLabel,
  recentSearchDedupeKey,
} from "./recent-search-utils";
import { getRecentSearches } from "./recent-searches";
import { recentToSearchParams } from "./search-params";

interface MobileSearchEntryProps {
  onClose: () => void;
  onSearch: (params: Record<string, string | number>, fromPointer?: boolean) => void;
  initialState?: Partial<SearchBarState>;
}

// fallow-ignore-next-line complexity
function parseInitialLocation(state: Partial<SearchBarState>): SelectedLocation | null {
  const { location, lat, lng, bounds } = state;
  if (!location && lat == null && lng == null && !bounds) return null;

  const trimmed = location?.trim();
  const label = trimmed && !isDefaultLocationLabel(trimmed) ? trimmed : DEFAULT_LOCATION_LABEL;
  const locationMode: SearchLocationMode | null =
    state.locationMode ?? (bounds ? "place" : lat != null && lng != null ? "coords" : null);

  return {
    label,
    lat: lat ?? null,
    lng: lng ?? null,
    bounds: bounds ?? null,
    locationMode,
  };
}

function parseInitialTime(whenTime: SearchBarState["whenTime"]): string | null {
  if (!whenTime || whenTime === "any") return null;
  return whenTime;
}

function isMobileSearchLocationSet(location: SelectedLocation | null): boolean {
  if (!location) return false;
  if (!isDefaultLocationLabel(location.label)) return true;
  return location.lat != null || location.lng != null || location.bounds != null;
}

// fallow-ignore-next-line complexity
function MobileSearchEntry({ onClose, onSearch, initialState }: MobileSearchEntryProps) {
  const isPresent = useIsPresent();
  const calendarId = useId();
  const { data: catData } = useServiceCategories();
  const cats = useMemo(() => {
    const raw = catData?.service_categories ?? [];
    return [...raw].sort((a, b) => a.display_order - b.display_order);
  }, [catData]);

  const [query, setQuery] = useState(() => initialState?.query ?? "");
  const [isNameSearchOpen, setIsNameSearchOpen] = useState(false);
  const previousFocusRef = useRef(document.activeElement);
  const debouncedQuery = useDebounce(query, DEBOUNCE_MS);

  const searchEnabled = debouncedQuery.trim().length >= 2;
  const {
    data: searchData,
    isLoading: isSearchLoading,
    isError: isSearchError,
    error: searchError,
    refetch: retrySearch,
    isFetching: isSearchFetching,
  } = useSearchVenues({ q: debouncedQuery.trim(), page: 1 }, searchEnabled);
  const liveResults = searchEnabled && !isSearchError ? (searchData?.results ?? []) : [];

  const [selectedCategoryKeys, setSelectedCategoryKeys] = useState<string[]>(() =>
    parseCategoryKeys(initialState?.category ?? undefined),
  );
  const selectedCategoryNames = useMemo(() => {
    const namesByKey = new Map(cats.map((cat) => [cat.key, cat.name]));
    return selectedCategoryKeys
      .map((key) => namesByKey.get(key))
      .filter((name): name is string => Boolean(name));
  }, [cats, selectedCategoryKeys]);
  const [selectedLocation, setSelectedLocation] = useState<SelectedLocation | null>(() =>
    parseInitialLocation(initialState ?? {}),
  );
  const [locationOverlayOpen, setLocationOverlayOpen] = useState(false);

  const [selectedDate, setSelectedDate] = useState<string | null>(initialState?.whenDate ?? null);
  const [selectedTime, setSelectedTime] = useState<string | null>(() =>
    parseInitialTime(initialState?.whenTime ?? null),
  );
  const [calendarOpen, setCalendarOpen] = useState(false);

  const today = useMemo(() => new Date(), []);
  const tomorrow = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d;
  }, []);

  const recents = useMemo(() => getRecentSearches(), []);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // fallow-ignore-next-line complexity
  function buildParams(extra?: Partial<RecentSearch>) {
    const params: RecentSearch = {
      q: query.trim() || undefined,
      category: selectedCategoryKeys.length > 0 ? selectedCategoryKeys.join(",") : undefined,
      categoryName: selectedCategoryNames.length > 0 ? selectedCategoryNames.join(", ") : undefined,
      location:
        selectedLocation?.label && !isDefaultLocationLabel(selectedLocation.label)
          ? selectedLocation.label
          : undefined,
      lat: selectedLocation?.lat ?? undefined,
      lng: selectedLocation?.lng ?? undefined,
      bounds: selectedLocation?.bounds ?? undefined,
      location_mode: selectedLocation?.locationMode ?? undefined,
      when_date: selectedDate ?? undefined,
      when_time: selectedTime && selectedTime !== "any" ? selectedTime : undefined,
      ...extra,
    };
    return recentToSearchParams(params);
  }

  function handleCategoryTap(key: string) {
    setSelectedCategoryKeys((prev) =>
      prev.includes(key) ? prev.filter((existing) => existing !== key) : [...prev, key],
    );
  }

  function handleCtaTap(event: MouseEvent<HTMLButtonElement>) {
    onSearch(buildParams(), event.detail > 0);
  }

  function handleTextSubmit() {
    if (!query.trim()) return;
    onSearch(buildParams({ q: query.trim() }));
  }

  function handleLiveResultTap(name: string) {
    onSearch(buildParams({ q: name }));
  }

  function handleRecentTap(recent: RecentSearch) {
    onSearch(recentToSearchParams(recent));
  }

  function handleBackClick() {
    if (isNameSearchOpen) {
      setIsNameSearchOpen(false);
      return;
    }
    onClose();
  }

  const isToday = selectedDate ? isSameDay(new Date(`${selectedDate}T00:00:00`), today) : false;
  const isTomorrow = selectedDate
    ? isSameDay(new Date(`${selectedDate}T00:00:00`), tomorrow)
    : false;
  const isCustomDate = selectedDate != null && !isToday && !isTomorrow;

  const content = (
    <motion.div
      className={cn(
        MOBILE_VISUAL_VIEWPORT_SHELL_CLASS,
        "z-50 flex flex-col bg-paper pt-[env(safe-area-inset-top)]",
      )}
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      exit={{ y: "100%" }}
      transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
    >
      <DialogPrimitive.Title className="sr-only">Search</DialogPrimitive.Title>
      <div className="shrink-0 border-b border-fog bg-paper px-3">
        <div className="flex items-center gap-2 py-2">
          <button
            type="button"
            className="inline-flex size-10 items-center justify-center rounded-full text-ink transition-colors hover:bg-fog"
            onClick={handleBackClick}
            aria-label={isNameSearchOpen ? "Back to filters" : "Close"}
          >
            <ArrowLeft className="size-5" strokeWidth={2} />
          </button>
          <div
            className={cn(
              "flex min-w-0 flex-1 items-center gap-2 rounded-pill border border-fog px-3.5 py-2 transition-colors",
              isNameSearchOpen ? "bg-surface" : "bg-transparent",
              isPresent && "search-route-bar",
            )}
          >
            <Search className="size-4 shrink-0 text-slate" />
            <input
              type="text"
              aria-label="Search by name"
              className="min-w-0 flex-1 bg-transparent text-[16px] text-ink outline-none placeholder:text-slate"
              placeholder="Search by name..."
              value={query}
              onFocus={() => setIsNameSearchOpen(true)}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") handleTextSubmit();
              }}
            />
            {query && (
              <button
                type="button"
                className="inline-flex size-6 items-center justify-center rounded-full text-slate transition-colors hover:bg-fog hover:text-ink"
                onClick={() => setQuery("")}
                aria-label="Clear search"
              >
                <X className="size-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div
        {...{ [KEYBOARD_SCROLL_CONTAINER_ATTR]: "" }}
        className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain"
      >
        {isNameSearchOpen ? (
          <div className="px-4 pt-3 pb-28">
            {searchEnabled && isSearchLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="size-5 animate-spin text-slate" />
              </div>
            )}

            {searchEnabled && isSearchError && (
              <SearchFailure
                error={searchError}
                onRetry={() => void retrySearch()}
                isRetrying={isSearchFetching}
              />
            )}

            {searchEnabled && !isSearchLoading && !isSearchError && liveResults.length > 0 && (
              <div className="flex flex-col gap-1">
                {liveResults.slice(0, 8).map((venue) => (
                  <button
                    key={venue.id}
                    type="button"
                    className="flex items-center gap-3 rounded-sm px-2 py-2.5 text-left transition-colors hover:bg-fog"
                    onClick={() => handleLiveResultTap(venue.name)}
                  >
                    <VenueInitial name={venue.name} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[14px] font-semibold text-ink">
                        {venue.name}
                      </div>
                      <div className="flex items-center gap-1 text-[12px] text-slate">
                        {venue.primary_category_name && <span>{venue.primary_category_name}</span>}
                        {venue.city && (
                          <>
                            {venue.primary_category_name && <span>·</span>}
                            <MapPin className="size-3" />
                            <span>{venue.city}</span>
                          </>
                        )}
                        {venue.distance_km != null && (
                          <span>· {venue.distance_km.toFixed(1)} km</span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {searchEnabled && !isSearchLoading && !isSearchError && liveResults.length === 0 && (
              <p className="py-8 text-center text-[14px] text-slate">
                No results for &ldquo;{debouncedQuery.trim()}&rdquo;
              </p>
            )}

            {!searchEnabled && recents.length > 0 && (
              <div>
                <span className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.06em] text-slate">
                  Recent searches
                </span>
                <div className="flex flex-col gap-1.5">
                  {recents.map((recent) => (
                    <button
                      key={recentSearchDedupeKey(recent)}
                      type="button"
                      className="flex items-center gap-2.5 rounded-sm border border-fog bg-surface px-3 py-2.5 text-left transition-colors hover:border-electric-700"
                      onClick={() => handleRecentTap(recent)}
                    >
                      <Clock className="size-3.5 shrink-0 text-slate" />
                      <span className="flex-1 truncate text-[14px] text-ink">
                        {formatRecentSearchLabel(recent)}
                      </span>
                      <ArrowLeft className="size-3.5 shrink-0 rotate-180 text-fog-dark" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!searchEnabled && recents.length === 0 && (
              <p className="py-8 text-center text-[14px] text-slate">
                Recent searches will appear here.
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="px-4 pt-2 pb-1">
              <h2 className="text-[20px] font-bold tracking-tight text-ink">
                What are you looking for?
              </h2>
            </div>

            {cats.length > 0 && (
              <div className="px-4 pt-3">
                <div className="grid grid-cols-3 gap-2.5">
                  {cats.map((cat) => {
                    const Icon = getCategoryIcon(cat.key);
                    const isSelected = selectedCategoryKeys.includes(cat.key);
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        aria-pressed={isSelected}
                        className={cn(
                          "flex min-h-[99px] flex-col items-center rounded-lg border px-2 py-3.5 transition-all duration-short",
                          isSelected
                            ? "border-electric-700 bg-electric-50 text-electric-800 shadow-[0_0_0_1px_var(--electric-700)]"
                            : "border-fog bg-surface text-ink hover:border-electric-700",
                        )}
                        onClick={() => handleCategoryTap(cat.key)}
                      >
                        <Icon
                          className="flex flex-1 items-center justify-center size-6"
                          strokeWidth={1.6}
                        />
                        <span className="flex flex-1 items-center justify-center text-center text-[13px] font-semibold leading-tight">
                          {cat.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="px-4 pt-4">
              <button
                type="button"
                className="flex w-full items-center gap-2.5 rounded-sm border border-fog bg-surface px-3.5 py-3 text-left transition-colors hover:border-electric-700"
                onClick={() => setLocationOverlayOpen(true)}
              >
                <MapPin className="size-4 shrink-0 text-slate" strokeWidth={1.8} />
                <span className="flex-1 text-[14px] font-medium text-ink">
                  {selectedLocation?.label ?? DEFAULT_LOCATION_LABEL}
                </span>
                <span className="text-[13px] font-medium text-electric-800">Change</span>
              </button>
            </div>

            <div className="px-4 pt-4">
              <div className="flex flex-wrap gap-2">
                <Chip
                  variant={isToday ? "active" : "default"}
                  aria-pressed={isToday}
                  onClick={() => {
                    setSelectedDate(isToday ? null : toISODate(today));
                    if (isToday) setSelectedTime(null);
                    setCalendarOpen(false);
                  }}
                >
                  Today
                </Chip>
                <Chip
                  variant={isTomorrow ? "active" : "default"}
                  aria-pressed={isTomorrow}
                  onClick={() => {
                    setSelectedDate(isTomorrow ? null : toISODate(tomorrow));
                    if (isTomorrow) setSelectedTime(null);
                    setCalendarOpen(false);
                  }}
                >
                  Tomorrow
                </Chip>
                <Chip
                  variant={isCustomDate || calendarOpen ? "active" : "default"}
                  aria-expanded={calendarOpen}
                  aria-controls={calendarId}
                  onClick={() => setCalendarOpen((open) => !open)}
                >
                  <CalendarDays className="size-3.5" />
                  {isCustomDate ? formatPickedDate(selectedDate) : "Pick a date"}
                </Chip>
              </div>

              <AnimatePresence initial={false}>
                {calendarOpen && (
                  <motion.div
                    key="mobile-search-calendar"
                    id={calendarId}
                    className="overflow-hidden"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    <MiniCalendar
                      selected={selectedDate}
                      today={today}
                      onSelect={(iso) => {
                        setSelectedDate(iso);
                        setCalendarOpen(false);
                      }}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="px-4 pt-2.5">
              <p className="mb-2 text-[12px] text-slate">
                Dates and times are local to each salon.
              </p>
              <div className="flex flex-wrap gap-2">
                {TIME_OPTIONS.map((opt) => (
                  <Chip
                    key={opt.value}
                    variant={(selectedTime ?? "any") === opt.value ? "active" : "default"}
                    aria-pressed={(selectedTime ?? "any") === opt.value}
                    onClick={() => {
                      const value = (selectedTime ?? "any") === opt.value ? null : opt.value;
                      if (value && value !== "any" && !selectedDate) {
                        setSelectedDate(toISODate(today));
                      }
                      setSelectedTime(value);
                    }}
                  >
                    {opt.label}
                  </Chip>
                ))}
              </div>
            </div>

            {recents.length > 0 && (
              <div className="px-4 pt-5 pb-28">
                <span className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.06em] text-slate">
                  Recent
                </span>
                <div className="flex flex-col gap-1.5">
                  {recents.map((recent) => (
                    <button
                      key={recentSearchDedupeKey(recent)}
                      type="button"
                      className="flex items-center gap-2.5 rounded-sm border border-fog bg-surface px-3 py-2.5 text-left transition-colors hover:border-electric-700"
                      onClick={() => handleRecentTap(recent)}
                    >
                      <Clock className="size-3.5 shrink-0 text-slate" />
                      <span className="flex-1 truncate text-[14px] text-ink">
                        {formatRecentSearchLabel(recent)}
                      </span>
                      <ArrowLeft className="size-3.5 shrink-0 rotate-180 text-fog-dark" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {recents.length === 0 && <div className="pb-28" />}
          </>
        )}
      </div>

      <div
        data-mobile-search-cta
        className={cn(
          "shrink-0 border-t border-fog bg-paper px-4 py-3",
          "pb-[calc(12px+env(safe-area-inset-bottom))]",
        )}
      >
        <button
          type="button"
          className="flex h-12 w-full items-center justify-center gap-2 rounded-pill bg-electric-700 text-[15px] font-semibold text-white transition-colors hover:bg-electric-800 focus-visible:outline-2 focus-visible:outline-electric-700 focus-visible:outline-offset-2"
          onClick={handleCtaTap}
        >
          <Search className="size-4" />
          Search
        </button>
      </div>

      <AnimatePresence>
        {locationOverlayOpen && (
          <MobileLocationOverlay
            onClose={() => setLocationOverlayOpen(false)}
            onSelect={(location) => {
              setSelectedLocation(location);
              setLocationOverlayOpen(false);
            }}
            showReset={isMobileSearchLocationSet(selectedLocation)}
            onReset={() => {
              setSelectedLocation(null);
              setLocationOverlayOpen(false);
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );

  return (
    <DialogPrimitive.Root open onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          asChild
          aria-modal="true"
          aria-describedby={undefined}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (previousFocusRef.current instanceof HTMLElement) previousFocusRef.current.focus();
          }}
        >
          {content}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function VenueInitial({ name }: { name: string }) {
  return (
    <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-electric-50 text-[14px] font-bold text-electric-800">
      {name.charAt(0).toUpperCase()}
    </span>
  );
}

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function formatPickedDate(iso: string): string {
  const d = parseISODate(iso);
  return d ? `${SHORT_MONTHS[d.getMonth()]} ${d.getDate()}` : "Choose a date";
}

function MiniCalendar({
  selected,
  today,
  onSelect,
}: {
  selected: string | null;
  today: Date;
  onSelect: (iso: string) => void;
}) {
  const initialDate = parseISODate(selected) ?? today;
  const [viewYear, setViewYear] = useState(initialDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialDate.getMonth());

  const maxDate = useMemo(() => getMaxDate(), []);

  const canGoPrev = viewYear > today.getFullYear() || viewMonth > today.getMonth();
  const canGoNext =
    viewYear < maxDate.getFullYear() ||
    (viewYear === maxDate.getFullYear() && viewMonth < maxDate.getMonth());

  const cells = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    let startDow = first.getDay() - 1;
    if (startDow < 0) startDow = 6;
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const out: { key: string; date: Date | null }[] = [];
    for (let i = 0; i < startDow; i++) out.push({ key: `p${i}`, date: null });
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(viewYear, viewMonth, day);
      out.push({ key: toISODate(date), date });
    }
    let trail = 0;
    while (out.length % 7 !== 0) out.push({ key: `t${trail++}`, date: null });
    return out;
  }, [viewYear, viewMonth]);

  const isPast = (date: Date) => {
    const atMidnight = new Date();
    atMidnight.setHours(0, 0, 0, 0);
    return date < atMidnight;
  };

  return (
    <div className="mt-3 rounded-lg border border-fog bg-surface p-3">
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => {
            if (!canGoPrev) return;
            if (viewMonth === 0) {
              setViewMonth(11);
              setViewYear(viewYear - 1);
            } else {
              setViewMonth(viewMonth - 1);
            }
          }}
          disabled={!canGoPrev}
          className="rounded-xs p-1 text-slate hover:text-ink disabled:opacity-30"
        >
          <ChevronLeft className="size-4" />
        </button>
        <span aria-live="polite" className="text-[13px] font-semibold text-ink">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </span>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => {
            if (!canGoNext) return;
            if (viewMonth === 11) {
              setViewMonth(0);
              setViewYear(viewYear + 1);
            } else {
              setViewMonth(viewMonth + 1);
            }
          }}
          disabled={!canGoNext}
          className="rounded-xs p-1 text-slate hover:text-ink disabled:opacity-30"
        >
          <ChevronRight className="size-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 text-center">
        {DAY_LABELS.map((label) => (
          <div key={label} className="py-0.5 text-[11px] font-medium text-slate">
            {label}
          </div>
        ))}
        {cells.map((cell) => {
          const date = cell.date;
          if (!date) return <div key={cell.key} className="py-1" />;
          const disabled = isPast(date) || isBeyondMaxDate(date);
          const selectedDay = selected === toISODate(date);
          const todayMark = isSameDay(date, today);
          return (
            <button
              key={cell.key}
              type="button"
              aria-label={date.toLocaleDateString("en-GB", {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
              aria-pressed={selectedDay}
              aria-current={todayMark ? "date" : undefined}
              disabled={disabled}
              className={cn(
                "rounded-full py-1 text-[13px] transition-colors",
                disabled && "cursor-not-allowed text-slate/40",
                !disabled && !selectedDay && "hover:bg-fog",
                selectedDay &&
                  "bg-electric-50 font-semibold text-electric-800 ring-1 ring-inset ring-electric-700",
                todayMark && !selectedDay && "font-semibold ring-1 ring-inset ring-fog-dark",
              )}
              onClick={() => onSelect(toISODate(date))}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export { MobileSearchEntry };
